import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, TrendingUp, TrendingDown, Wallet, CreditCard, DollarSign, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  'income',
  'expense',
  'investment',
  'recovery',
  'transfer',
  'salary',
  'commission',
  'investor_profit',
  'other',
];
const CATEGORY_LABELS = {
  income: 'Income',
  expense: 'Expense',
  investment: 'Investment',
  recovery: 'Recovery',
  transfer: 'Transfer',
  salary: 'Salary Payout',
  commission: 'Commission Payout',
  investor_profit: 'Investor Profit Payout',
  other: 'Other',
};

export default function FinanceModule() {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    account_type: 'cash', tx_type: 'credit', category: 'income',
    amount: '', description: '', reference: '',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API_URL}/api/finance/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/finance/transactions`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setTransactions(t.data);
    } catch { toast.error('Failed to load finance data'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/finance/transactions`,
        { ...form, amount: Number(form.amount) },
        { withCredentials: true });
      toast.success('Transaction recorded');
      setDialogOpen(false);
      setForm({ account_type: 'cash', tx_type: 'credit', category: 'income', amount: '', description: '', reference: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this transaction?')) return;
    try {
      await axios.delete(`${API_URL}/api/finance/transactions/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const filterTx = (account) => transactions.filter(t => t.account_type === account);
  const fmt = (n) => `PKR ${(n || 0).toLocaleString()}`;

  const renderLedger = (account) => {
    const rows = filterTx(account);
    return rows.length === 0 ? (
      <div className="text-center py-8 text-slate-400">No {account} transactions yet</div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-400">Date</TableHead>
            <TableHead className="text-slate-400">Category</TableHead>
            <TableHead className="text-slate-400">Type</TableHead>
            <TableHead className="text-slate-400">Description</TableHead>
            <TableHead className="text-slate-400 text-right">Amount</TableHead>
            <TableHead className="text-slate-400"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="finance-row">
              <TableCell className="text-slate-300 text-xs">{new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
              <TableCell className="text-slate-300 capitalize">{CATEGORY_LABELS[r.category] || r.category}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 rounded-sm text-xs ${r.tx_type === 'credit' ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
                  {r.tx_type}
                </span>
              </TableCell>
              <TableCell className="text-slate-300 max-w-md truncate">{r.description || '—'}</TableCell>
              <TableCell className={`text-right font-medium ${r.tx_type === 'credit' ? 'text-emerald-500' : 'text-red-500'}`}>
                {r.tx_type === 'credit' ? '+' : '-'} {fmt(r.amount)}
              </TableCell>
              <TableCell>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} className="text-red-500 hover:bg-slate-800 h-8 w-8 p-0"><Trash2 className="h-3 w-3" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const cards = summary ? [
    { title: 'Cash Balance', value: fmt(summary.cash_balance), icon: Wallet, color: 'text-emerald-500' },
    { title: 'Bank Balance', value: fmt(summary.bank_balance), icon: CreditCard, color: 'text-blue-500' },
    { title: 'Investment', value: fmt(summary.total_investment), icon: DollarSign, color: 'text-amber-500' },
    { title: 'Gross Profit', value: fmt(summary.gross_profit), icon: TrendingUp, color: 'text-emerald-500' },
    { title: 'Total Income', value: fmt(summary.total_income), icon: TrendingUp, color: 'text-emerald-500' },
    { title: 'Total Expenses', value: fmt(summary.total_expenses), icon: TrendingDown, color: 'text-red-500' },
    { title: 'Commissions Paid', value: fmt(summary.total_commissions_paid || 0), icon: TrendingDown, color: 'text-red-400' },
    { title: 'Salaries Paid', value: fmt(summary.total_salaries_paid || 0), icon: TrendingDown, color: 'text-red-400' },
    { title: 'Investor Profit Paid', value: fmt(summary.total_investor_profit || 0), icon: TrendingDown, color: 'text-red-400' },
    { title: 'Net Profit', value: fmt(summary.net_profit), icon: TrendingUp, color: summary.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500' },
    { title: 'Outstanding', value: fmt(summary.outstanding), icon: DollarSign, color: 'text-amber-500' },
  ] : [];

  return (
    <div className="space-y-6" data-testid="finance-module">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="finance-title">Finance</h1>
          <p className="text-slate-400">Cash, Bank ledgers and live P&amp;L</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-tx-button"><Plus className="h-4 w-4 mr-2" /> New Transaction</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-white">Record Transaction</DialogTitle>
              <DialogDescription className="text-slate-400">Credit = money in, Debit = money out.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Account *</Label>
                  <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="tx-account"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Type *</Label>
                  <Select value={form.tx_type} onValueChange={(v) => setForm({ ...form, tx_type: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="tx-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="credit">Credit (Money In)</SelectItem>
                      <SelectItem value="debit">Debit (Money Out)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Category *</Label>
                  <Select value={form.category} onValueChange={(v) => {
                    // Payout categories automatically imply a debit
                    const isPayout = ['salary', 'commission', 'investor_profit', 'expense'].includes(v);
                    setForm({ ...form, category: v, tx_type: isPayout ? 'debit' : (v === 'income' || v === 'investment' ? 'credit' : form.tx_type) });
                  }}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{CATEGORY_LABELS[c] || c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Amount (PKR) *</Label>
                  <Input type="number" data-testid="tx-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-300">Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-300">Reference</Label>
                  <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">Cancel</Button>
                <Button type="submit" data-testid="submit-tx" className="bg-amber-500 hover:bg-amber-600">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="bg-slate-900 border-slate-800" data-testid={`finance-card-${i}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wider">{c.title}</span>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </CardHeader>
            <CardContent><div className="text-xl font-bold text-white">{c.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <Tabs defaultValue="cash">
            <TabsList className="bg-slate-800 border border-slate-700">
              <TabsTrigger value="cash" data-testid="tab-cash" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Cash Ledger</TabsTrigger>
              <TabsTrigger value="bank" data-testid="tab-bank" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Bank Ledger</TabsTrigger>
            </TabsList>
            <TabsContent value="cash" className="mt-4">{renderLedger('cash')}</TabsContent>
            <TabsContent value="bank" className="mt-4">{renderLedger('bank')}</TabsContent>
          </Tabs>
        </CardHeader>
      </Card>
    </div>
  );
}
